import classNames from 'clsx';
import { isUndefined } from 'lodash';
import React, { useCallback } from 'react';
import type { ReactText } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { RouteComponentProps } from 'react-router';

import { ResolverCMSContent } from '@swf/cms';
import RawButton from '@swf/components/StarsButton/RawButton';
import StarsButton from '@swf/components/StarsButton/StarsButton';
import StarsInfo from '@swf/components/StarsInfo/StarsInfo';
import Body from '@swf/components/StarsModal/components/Body';
import CloseButton from '@swf/components/StarsModal/components/CloseButton';
import Header from '@swf/components/StarsModal/components/Header/Header';
import Translation from '@swf/components/Translation/Translation';
import { ResolverTag } from '@swf/core/enums';
import { select } from '@swf/core/store';
import type { Dispatch } from '@swf/core/store';
import type { TrackedTranslation } from '@swf/core/types/i18n';

import {
  TXTCLI_Back,
  TXTWCL_Accept_Terms_X_Conditions,
  TXTWCL_HereXs_how_to_take_part,
  TXTWCL_Terms_and_Conditions,
  TXTWCL_XfirstXThe_Races_Terms_X_Condi_ELL
} from './CasinoHelp.messages.json';
import styles from './CasinoHelp.scss';

const CasinoHelp: React.FC<
  RouteComponentProps<
    object,
    object,
    {
      title: string;
      buttonText: string;
      buttonTextTag?: TrackedTranslation;
      acceptanceRequired: boolean;
      version: number;
      pathname?: string;
    }
  >
> = ({
  location: {
    state: { title, buttonText, acceptanceRequired, version, buttonTextTag, pathname }
  }
}) => {
  const { casino, modal } = useDispatch<Dispatch>();
  const isMobile = useSelector(select.device.isMobile);
  const legacyLiveAccepted = useSelector(select.casino.liveRacesTermsAccepted);
  const legacyUKAccepted = useSelector(select.casino.UKTermsAccepted);
  const acceptedVersion = useSelector(select.casino.racesTermsVersionAccepted);
  const haveAcceptedAnyTerms =
    legacyLiveAccepted || legacyUKAccepted || !isUndefined(acceptedVersion);
  const showReacceptMsg = acceptanceRequired && haveAcceptedAnyTerms;
  const text = (text: ReactText) => <span className={styles.ownLine}>{text}</span>;

  const handleAccept = useCallback(() => {
    casino.acceptRacesTerms(version);
  }, [version, casino]);

  return (
    <>
      <Header>
        {acceptanceRequired ? (
          <Translation {...TXTWCL_Terms_and_Conditions} />
        ) : (
          title || <Translation {...TXTWCL_HereXs_how_to_take_part} />
        )}
        <CloseButton />
      </Header>
      <Body
        className={classNames(
          styles.body,
          !showReacceptMsg && styles.topPadding,
          isMobile && styles.mobileDevice
        )}
      >
        {showReacceptMsg && (
          <StarsInfo className={styles.info}>
            <Translation
              {...TXTWCL_XfirstXThe_Races_Terms_X_Condi_ELL}
              values={{ first: text, second: text }}
            />
          </StarsInfo>
        )}
        <div className={styles.scrollableArea}>
          <ResolverCMSContent tag={ResolverTag.PRMCasinoRacesIngame} pathname={pathname} />
        </div>
        {acceptanceRequired ? (
          <StarsButton
            block
            onClick={handleAccept}
            translation={TXTWCL_Accept_Terms_X_Conditions}
          />
        ) : (
          <RawButton className={styles.button} block onClick={() => modal.close()}>
            {buttonText || <Translation {...(buttonTextTag || TXTCLI_Back)} />}
          </RawButton>
        )}
      </Body>
    </>
  );
};

export default CasinoHelp;
